import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useUnifiedAuth } from '@/hooks/useUnifiedAuth';
import { useAppSelector } from '@/store/hooks';
import {
  fetchDashboardTotals,
  fetchDashboardByMaterialPerDay,
  fetchDashboardFaultReasons,
  fetchDashboardVolumeByEstablishment,
  fetchDashboardCollectsByOrigin,
  fetchDashboardCarbonByMaterial,
  type DashboardFilterInput,
  type DashboardTotals,
  type DashboardByMaterialPerDay,
  type EstablishmentVolumePerDay,
  type FaultReasonBreakdown,
  type MaterialCarbonBreakdown,
  type MonthlyCollectsByOrigin,
} from '@/integrations/agreements/client';
import type { FilterState } from '@/hooks/useFilters';
import {
  clampDateRangeForCutoff,
  excludeCutoffMonthlyItems,
  resolveEffectiveGeneratorIds,
} from '@/config/legacyResicleanCutoff';

function toYYYYMMDD(d: Date): string {
  return d.toISOString().split('T')[0];
}

/**
 * Mapeia FilterState + perfil para o filtro esperado pela API Dashboard.
 * Todos os filtros são enviados como arrays (backend suportará arrays).
 * Quando vazio ou não selecionado, envia null (sem filtro).
 */
export function buildDashboardFilter(
  filters: FilterState,
  generatorsFromRedux: Array<{ id: string; external_id: string }>,
  establishmentsFromRedux: Array<{ id: string; external_id: string; parent_id: number | string | null }>,
  profileGeneratorId: number | null | undefined,
): DashboardFilterInput {
  const rawDateFrom = filters?.dateRange?.start ? toYYYYMMDD(filters.dateRange.start) : toYYYYMMDD(new Date());
  const rawDateTo = filters?.dateRange?.end ? toYYYYMMDD(filters.dateRange.end) : toYYYYMMDD(new Date());

  // Geradores: converter external_id para id numérico.
  // `generatorsFromRedux` já vem escopado pelo backend via JWT (useFilterOptions),
  // então qualquer perfil pode usar o filtro manual quando selecionado.
  // Sem filtro manual, não restringe: usuário externo com múltiplos geradores
  // (UserGeneratorLink) deve ver o total combinado, não só o gerador "principal"
  // do perfil — o backend já escopa a resposta pelo que o JWT permite ver.
  let generator_id: number[] | null = null;
  if (filters?.generators?.length) {
    const generatorIds = filters.generators
      .map((externalId) => {
        const found = generatorsFromRedux.find(
          (g) => g.external_id === externalId || g.id === externalId
        );
        return found ? parseInt(found.id, 10) : null;
      })
      .filter((id): id is number => id !== null);
    generator_id = generatorIds.length > 0 ? generatorIds : null;
  }

  // Materiais: converter string IDs para números
  const material_id: number[] | null =
    filters?.materials && filters.materials.length > 0
      ? filters.materials
          .map((id) => {
            const n = parseFloat(String(id));
            return Number.isNaN(n) ? null : n;
          })
          .filter((id): id is number => id !== null)
      : null;

  // Estados/UF: array de strings
  const uf: string[] | null =
    filters?.states && filters.states.length > 0 ? filters.states : null;

  // Estabelecimentos: converter external_id para id numérico
  const establishment_id: number[] | null =
    filters?.establishments && filters.establishments.length > 0
      ? filters.establishments
          .map((externalId) => {
            const found = establishmentsFromRedux.find(
              (e) => e.external_id === externalId || e.id === externalId
            );
            return found ? parseInt(found.id, 10) : null;
          })
          .filter((id): id is number => id !== null)
      : null;

  // O gerador vai como `generatorId` e o agreements expande a subárvore no servidor. A
  // derivação em filiais que existia aqui (herança do gateway, que ignorava `generator_id`)
  // mandava centenas de `establishmentId` na URL e estourava o limite (414) em gerador grande.

  // MC-107: recorta o período com coletas retroativas fictícias (jan-jul/2026)
  // quando a consulta é inteiramente escopada a gerador(es) da carteira
  // Resiclean onboardados sem esses dados. Ver src/config/legacyResicleanCutoff.ts.
  const effectiveGeneratorIds = resolveEffectiveGeneratorIds(generator_id, profileGeneratorId);
  const { date_from, date_to } = clampDateRangeForCutoff(effectiveGeneratorIds, rawDateFrom, rawDateTo);

  // Construir objeto de filtro
  const filter: DashboardFilterInput = {
    date_from,
    date_to,
    generator_id,
    material_id,
    uf,
  };

  // Incluir establishment_id quando tiver valor
  if (establishment_id != null && establishment_id.length > 0) {
    filter.establishment_id = establishment_id;
  }

  return filter;
}

export interface UseDashboardApiDataParams {
  filters?: FilterState;
  /** Quando true, não chama a API. */
  skip?: boolean;
}

/** Filtro compartilhado pelos 4 hooks de seção do Dashboard. */
function useDashboardFilterInput(params?: UseDashboardApiDataParams) {
  const { profile } = useUnifiedAuth();
  const { filters, skip = false } = params ?? {};
  const generatorsFromRedux = useAppSelector((s) => s.establishmentAccess.generators);
  const establishmentsFromRedux = useAppSelector((s) => s.establishmentAccess.establishments);

  const filterInput: DashboardFilterInput | null =
    filters?.dateRange?.start && filters?.dateRange?.end
      ? buildDashboardFilter(filters, generatorsFromRedux, establishmentsFromRedux, profile?.generator_id)
      : null;

  return { profile, filterInput, skip };
}

const EMPTY_TOTALS: DashboardTotals = {
  mtr_rate: null,
  success_rate: 0,
  total_collects: 0,
  total_collects_previous: 0,
  variation_percent: null,
  total_units: 0,
  total_volume: 0,
  total_weight: 0,
  total_establishments_with_collects: 0,
};

/** KPIs do Overview — query própria, independente das outras seções. */
export function useDashboardTotals(params?: UseDashboardApiDataParams) {
  const { profile, filterInput, skip } = useDashboardFilterInput(params);

  return useQuery({
    queryKey: ['dashboard-totals', filterInput, skip],
    queryFn: ({ signal }): Promise<DashboardTotals> =>
      filterInput ? fetchDashboardTotals(filterInput, signal) : Promise.resolve(EMPTY_TOTALS),
    enabled: !!profile && !!filterInput && !skip,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/** Série por material/dia — query própria, independente das outras seções. */
export function useDashboardByMaterialPerDay(params?: UseDashboardApiDataParams) {
  const { profile, filterInput, skip } = useDashboardFilterInput(params);

  return useQuery({
    queryKey: ['dashboard-by-material-per-day', filterInput, skip],
    queryFn: ({ signal }): Promise<DashboardByMaterialPerDay[]> =>
      filterInput ? fetchDashboardByMaterialPerDay(filterInput, signal) : Promise.resolve([]),
    enabled: !!profile && !!filterInput && !skip,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/** Volume por unidade no grão diário — mapa de calor "Volume coletado". */
export function useDashboardVolumeByEstablishment(params?: UseDashboardApiDataParams) {
  const { profile, filterInput, skip } = useDashboardFilterInput(params);

  return useQuery({
    queryKey: ['dashboard-volume-by-establishment', filterInput, skip],
    queryFn: ({ signal }): Promise<EstablishmentVolumePerDay[]> =>
      filterInput ? fetchDashboardVolumeByEstablishment(filterInput, undefined, signal) : Promise.resolve([]),
    enabled: !!profile && !!filterInput && !skip,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Filtro sem `date_from`/`date_to`, para as seções cuja janela é relativa a
 * hoje e ignora o date picker.
 *
 * O `queryKey` das seções assim tem que usar este escopo, não o filtro
 * completo: senão mexer nas datas dispara um refetch que devolve exatamente a
 * mesma resposta.
 */
function useDashboardScopeInput(params?: UseDashboardApiDataParams) {
  const { profile, filterInput, skip } = useDashboardFilterInput(params);

  const scopeInput = useMemo(() => {
    if (!filterInput) return null;
    const { date_from: _from, date_to: _to, ...scope } = filterInput;
    return scope;
  }, [filterInput]);

  return { profile, scopeInput, skip };
}

/**
 * Coletas recorrentes x sob demanda — janela fixa de 4 meses, também alheia ao
 * date picker.
 */
export function useDashboardCollectsByOrigin(
  params?: UseDashboardApiDataParams,
) {
  const { profile, scopeInput, skip } = useDashboardScopeInput(params);
  // MC-107: essa seção não manda date_from/date_to (janela alheia ao date
  // picker) — o clamp de buildDashboardFilter não se aplica aqui. Cada item
  // da resposta tem `month`, então filtra direto o recorte oculto.
  const effectiveGeneratorIds = resolveEffectiveGeneratorIds(
    scopeInput?.generator_id ?? null,
    profile?.generator_id ?? null,
  );

  return useQuery({
    queryKey: ['dashboard-collects-by-origin', scopeInput, skip],
    queryFn: async ({ signal }): Promise<MonthlyCollectsByOrigin[]> => {
      const data = scopeInput ? await fetchDashboardCollectsByOrigin(scopeInput, signal) : [];
      return excludeCutoffMonthlyItems(data, (item) => item.month, effectiveGeneratorIds);
    },
    enabled: !!profile && !!scopeInput && !skip,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/** Carbono evitado por resíduo — widget do Overview, query própria. */
export function useDashboardCarbonByMaterial(params?: UseDashboardApiDataParams) {
  const { profile, filterInput, skip } = useDashboardFilterInput(params);

  return useQuery({
    queryKey: ['dashboard-carbon-by-material', filterInput, skip],
    queryFn: ({ signal }): Promise<MaterialCarbonBreakdown[]> =>
      filterInput ? fetchDashboardCarbonByMaterial(filterInput, undefined, signal) : Promise.resolve([]),
    enabled: !!profile && !!filterInput && !skip,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/** Ranking de motivos de falha — widget "Taxa de sucesso", query própria, independente das outras seções. */
export function useDashboardFaultReasons(params?: UseDashboardApiDataParams) {
  const { profile, filterInput, skip } = useDashboardFilterInput(params);

  return useQuery({
    queryKey: ['dashboard-fault-reasons', filterInput, skip],
    queryFn: ({ signal }): Promise<FaultReasonBreakdown[]> =>
      filterInput ? fetchDashboardFaultReasons(filterInput, undefined, signal) : Promise.resolve([]),
    enabled: !!profile && !!filterInput && !skip,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
